import {
  OrganizationService,
  PaymentMethodTypesEnum,
  TransactionFlowTypesEnum,
} from "@propertelligent/client-api";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useEffect, useMemo } from "react";
import RightSideModal from "../Modal/right-side-modal";
import InputField from "../form-fields/InputField";
import SelectField from "../form-fields/SelectField";
import Card from "../card";
import apiHelper from "../../utils/apiHelper";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import DateHelper from "../../utils/DateHelper";
import { getEnumOptions } from "../../utils/enumHelpers";
import { useFieldChangesSnapshot } from "../../hooks/useFieldChangesSnapshot";
import type { TransactionRow } from "./transactionTypes";

interface DropdownOption {
  id?: number | null;
  accountName?: string | null;
  paymentMethodTypeId?: PaymentMethodTypesEnum | null;
}

interface EditTransactionDrawerProps {
  isOpen: boolean;
  transaction: TransactionRow | null;
  paymentMethods: DropdownOption[];
  payeeAccounts: DropdownOption[];
  onClose: () => void;
  onSaved: () => void;
}

interface EditTransactionFormValues {
  description: string;
  paymentMethodId: string;
  payeeAccountId: string;
  tranactionCreditDebitTypeId: string;
  amount: string;
  paymentDate: string;
  reference: string;
  transactionId: string;
}

const formId = "edit-transaction-form";
const TRANSACTION_AMOUNT_MAX = 99999999.99;
const TRANSACTION_AMOUNT_MAX_MESSAGE =
  "Amount cannot exceed 99,999,999.99.";

const toDateInputValue = (paymentDate?: string) =>
  paymentDate ? DateHelper.convertUTCtoDateTime2(paymentDate) : "";

const toDefaultValues = (
  transaction: TransactionRow | null,
): EditTransactionFormValues => ({
  description: transaction?.description ?? "",
  paymentMethodId: transaction?.paymentMethodId
    ? String(transaction.paymentMethodId)
    : "",
  payeeAccountId: transaction?.payeeAccountId
    ? String(transaction.payeeAccountId)
    : "",
  tranactionCreditDebitTypeId: transaction?.tranactionCreditDebitTypeId != null
    ? String(transaction.tranactionCreditDebitTypeId)
    : "",
  amount:
    transaction?.amount != null && !Number.isNaN(Number(transaction.amount))
      ? String(transaction.amount)
      : "",
  paymentDate: toDateInputValue(transaction?.paymentDate),
  reference: transaction?.reference ?? "",
  transactionId: transaction?.transactionId ?? "",
});

const EditTransactionDrawer = ({
  isOpen,
  transaction,
  paymentMethods,
  payeeAccounts,
  onClose,
  onSaved,
}: EditTransactionDrawerProps) => {
  const methods = useForm<EditTransactionFormValues>({
    mode: "onChange",
    defaultValues: toDefaultValues(transaction),
  });

  // RHF isDirty stays true after select revert when default was string and live value
  // is number ("11" !== 11). Snapshot String()-compares so Save re-disables correctly.
  const { hasFieldChanges, setSnapshot } = useFieldChangesSnapshot<EditTransactionFormValues>(
    methods.control,
    [
      "description",
      "paymentMethodId",
      "payeeAccountId",
      "tranactionCreditDebitTypeId",
      "amount",
      "paymentDate",
      "reference",
      "transactionId",
    ],
  );

  useEffect(() => {
    const defaults = toDefaultValues(transaction);
    methods.reset(defaults);
    setSnapshot(defaults);
  }, [methods, transaction, setSnapshot]);

  const {
    handleSubmit,
    formState: { isDirty, isSubmitting, isValid },
  } = methods;

  const hasUnsavedChanges = isDirty && hasFieldChanges;

  // Payment Method lists METHODS (Cash, Check, Plaid, ...), one option per method type, never
  // account names (those are the "From" data). Each option's value is a payment-method row id of
  // that type: the transaction's own row for its current type, otherwise the org's first row of
  // that type (Cash/Check/Assisted/Other are single seeded rows).
  // A Plaid-settled transaction's method is locked, and Plaid is offered only when it is the
  // current method: Plaid is bound to a specific connected account (the "From"), Delete/Refund
  // eligibility keys off the method's type (Plaid rows are not deletable; Refund is for Plaid),
  // and there is no account picker here, so switching to/from Plaid would silently rebind the
  // account or bypass those rules.
  const isPlaidTransaction =
    transaction?.paymentMethodTypeId === PaymentMethodTypesEnum.Plaid;

  const paymentMethodOptions = useMemo(() => {
    const optionByType = new Map<
      PaymentMethodTypesEnum,
      { Id: number; Name: string; TypeId?: PaymentMethodTypesEnum }
    >();
    const currentId = transaction?.paymentMethodId;
    const currentTypeId = transaction?.paymentMethodTypeId;

    if (currentId && currentTypeId != null) {
      optionByType.set(currentTypeId, {
        Id: currentId,
        TypeId: currentTypeId,
        Name:
          transaction?.formatted?.PaymentMethodTypeId ??
          PaymentMethodTypesEnum[currentTypeId],
      });
    }

    paymentMethods.forEach((item) => {
      const typeId = item?.paymentMethodTypeId;
      if (
        Number(item?.id) > 0 &&
        typeId != null &&
        typeId !== PaymentMethodTypesEnum.Default &&
        typeId !== PaymentMethodTypesEnum.Plaid &&
        !optionByType.has(typeId)
      ) {
        optionByType.set(typeId, {
          Id: Number(item.id),
          TypeId: typeId,
          Name: PaymentMethodTypesEnum[typeId],
        });
      }
    });

    // Rows without a known type: keep showing the current one rather than a blank select.
    if (currentId && currentTypeId == null) {
      return [
        {
          Id: currentId,
          Name: transaction?.formatted?.PaymentMethodTypeId ?? "—",
          TypeId: undefined,
        },
        ...optionByType.values(),
      ];
    }

    return Array.from(optionByType.values());
  }, [
    paymentMethods,
    transaction?.paymentMethodId,
    transaction?.paymentMethodTypeId,
    transaction?.formatted,
  ]);

  // The saved method type must follow the selected option, not the transaction's original
  // one: the backend validates paymentMethodTypeId from the payload (e.g. Other requires a
  // Transaction ID), so a stale type would skip or wrongly enforce that rule after a change.
  const selectedPaymentMethodId = useWatch({
    control: methods.control,
    name: "paymentMethodId",
  });
  const selectedPaymentMethodTypeId =
    paymentMethodOptions.find(
      (option) => String(option.Id) === String(selectedPaymentMethodId),
    )?.TypeId ?? transaction?.paymentMethodTypeId;

  const payeeAccountOptions = useMemo(
    () =>
      payeeAccounts
        .filter((item) => Number(item?.id) > 0 && item?.accountName)
        .map((item) => ({
          Id: Number(item.id),
          Name: item.accountName as string,
        })),
    [payeeAccounts],
  );

  const transactionFlowOptions = useMemo(
    () =>
      getEnumOptions(
        TransactionFlowTypesEnum,
        Object.values(TransactionFlowTypesEnum).filter(
          (value): value is number => typeof value === "number",
        ),
      ),
    [],
  );

  const onSubmit = async (values: EditTransactionFormValues) => {
    if (!transaction?.id) {
      return;
    }

    const payload = {
      id: transaction.id,
      description: values.description.trim(),
      paymentMethodId: Number(values.paymentMethodId),
      payeeAccountId: Number(values.payeeAccountId),
      tranactionCreditDebitTypeId: Number(values.tranactionCreditDebitTypeId),
      amount: Number(values.amount),
      paymentDate: new Date(values.paymentDate).toISOString(),
      reference: values.reference.trim(),
      transactionId: values.transactionId.trim(),
      paymentDueAccountId: transaction.paymentDueAccountId,
      paymentMethodTypeId: selectedPaymentMethodTypeId,
      assistanceProvider:
        selectedPaymentMethodTypeId === PaymentMethodTypesEnum.Assisted
          ? transaction.assistanceProvider ?? null
          : null,
      paymentMethodAccountName: transaction.paymentMethodAccountName ?? null,
      payeeAccountName: transaction.payeeAccountName ?? null,
    };

    try {
      const response = await apiHelper(
        OrganizationService.putApiOrganizationTransaction,
        {
          successMessage: "Successfully Updated Transaction",
          showNotification: true,
        },
        String(transaction.id),
        payload,
      );

      if (response?.data?.success) {
        onClose();
        onSaved();
        return;
      }

      if (!response?.statusCode && !response?.errors) {
        PubSub.publish(TopicsEnum.Notification.toString(), {
          type: NotificationType.Failure,
          message: "Failed to update transaction. Try again.",
        });
      }
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        type: NotificationType.Failure,
        message: "Failed to update transaction. Try again.",
      });
    }
  };

  return (
    <RightSideModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Transaction"
      buttonlabel={isSubmitting ? "Saving..." : "Save"}
      formId={formId}
      maxWidth={550}
      isDirty={hasUnsavedChanges}
      isLoading={isSubmitting}
      isSubmitDisabled={isSubmitting || !hasUnsavedChanges || !isValid}
    >
      <Card className="no-bg">
        <FormProvider {...methods}>
          <form id={formId} onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-md-12 mb-1">
                <InputField
                  name="description"
                  label="Description"
                  placeholder="Enter Description"
                  allowSpecialChars={true}
                />
              </div>

              <div className="col-md-12 mb-1">
                <SelectField
                  name="paymentMethodId"
                  label="Payment Method"
                  options={paymentMethodOptions}
                  placeholderText="Select Payment Method"
                  disabled={isPlaidTransaction}
                />
              </div>

              <div className="col-md-12 mb-1">
                <SelectField
                  name="payeeAccountId"
                  label="Payee Account"
                  options={payeeAccountOptions}
                  placeholderText="Select Payee Account"
                />
              </div>

              <div className="col-md-12 mb-1">
                <SelectField
                  name="tranactionCreditDebitTypeId"
                  label="Category"
                  options={transactionFlowOptions}
                  placeholderText="Select Category"
                />
              </div>

              <div className="col-md-6 mb-1">
                <InputField
                  name="amount"
                  label="Amount"
                  type="number"
                  required
                  requiredMessage="Amount is required"
                  placeholder="Enter Amount"
                  validation={{
                    validate: (value) => {
                      const amount = Number(value);
                      if (!(amount > 0)) {
                        return "Amount must be greater than 0";
                      }
                      if (amount > TRANSACTION_AMOUNT_MAX) {
                        return TRANSACTION_AMOUNT_MAX_MESSAGE;
                      }
                      return true;
                    },
                  }}
                  min="0.01"
                  allowDecimal={true}
                />
              </div>

              <div className="col-md-6 mb-1">
                <InputField
                  name="paymentDate"
                  label="Date"
                  type="date"
                  required
                  requiredMessage="Date is required"
                />
              </div>

              <div className="col-md-12 mb-1">
                <InputField
                  name="reference"
                  label="Reference"
                  placeholder="Enter Reference"
                  allowSpecialChars={true}
                />
              </div>

              <div className="col-md-12 mb-1">
                <InputField
                  name="transactionId"
                  label="Transaction ID"
                  required={
                    selectedPaymentMethodTypeId === PaymentMethodTypesEnum.Other
                  }
                  requiredMessage="Transaction ID is required"
                  placeholder="Enter Transaction ID"
                  allowSpecialChars={true}
                />
              </div>
            </div>
          </form>
        </FormProvider>
      </Card>
    </RightSideModal>
  );
};

export default EditTransactionDrawer;
